import React, {useCallback, useEffect, useState} from 'react';
import {Link, Navigate, useParams} from 'react-router-dom';
import {useWardrobe} from '../context/WardrobeContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {DisposalOverview, fetchDisposalOverview} from '../service/DisposalApi';
import DisposalQueuePage from './DisposalQueuePage';
import DeclutterPage from './DeclutterPage';
import TrashBinPage from './TrashBinPage';
import DisposedItemsPage from './DisposedItemsPage';
import {pluralItems} from '../utils/plural';
import logger from '../utils/logger';

/**
 * Раздел «Выбытие» — одна страница со стадиями вместо трёх пунктов меню.
 *
 * <p>Кандидаты, решения, ожидание передачи и архив — это не четыре независимых экрана, а
 * четыре состояния одной вещи во времени. Разные числа на них законны; непонятно было
 * другое — что это один процесс. Поэтому стадии стоят рядом, счётчик у каждой свой и ведёт
 * ровно к своей выборке, а адрес стадии живёт в URL, чтобы на неё можно было сослаться.</p>
 *
 * <p>Домашние и рабочие вещи сюда не попадают ни на одной стадии: смена назначения оставляет
 * вещь в гардеробе, и шкаф от неё не освобождается. Их число показано отдельной строкой —
 * именно потому, что в счётчики выбытия его класть нельзя.</p>
 *
 * <p>Счётчики выглядят одинаково, но означают разное — очередь, прогресс, ожидание, событие.
 * Поэтому у каждого есть доступное имя с расшифровкой, а стадия решений показывает дробь
 * «обработано из очереди», а не одно число, которое читалось как «столько уже решено».</p>
 */

export type DisposalStage = 'candidates' | 'decisions' | 'pending' | 'archive';

/** Что показывает счётчик стадии: число и то, как его прочитать вслух. */
interface StageCount {
    /** Текст в бейдже: обычно число, у стадии решений — «k/N». */
    text: string;
    /** Полная фраза для скринридера и подсказки: счётчики визуально одинаковы, смысл — разный. */
    label: string;
}

interface StageDef {
    key: DisposalStage;
    label: string;
    /** Одна фраза о том, на каком вопросе стоит человек на этой стадии. */
    hint: string;
    count: (o: DisposalOverview) => StageCount | null;
}

const plainCount = (value: number | null | undefined, label: (n: number) => string): StageCount | null =>
    value == null || value <= 0 ? null : {text: String(value), label: label(value)};

/**
 * Счётчик стадии решений. «Решения 10» читалось как десять уже принятых решений, хотя означало
 * очередь из десяти, где решено ноль. Поэтому здесь дробь: слева — сколько обработано.
 * Старый бэкенд без `decisionTotalCount` отдаёт одно число — показываем его как раньше.
 */
const decisionsCount = (o: DisposalOverview): StageCount | null => {
    if (o.decisionCount == null) {
        return null;
    }
    if (o.decisionTotalCount == null) {
        return plainCount(o.decisionCount, n => `На рассмотрении: ${n} вещей ждут решения`);
    }
    const done = Math.max(0, o.decisionTotalCount - o.decisionCount);
    return {
        text: `${done}/${o.decisionTotalCount}`,
        label: `На рассмотрении: обработано ${done} из ${o.decisionTotalCount}`,
    };
};

export const DISPOSAL_STAGES: StageDef[] = [
    {
        key: 'candidates',
        label: 'Кандидаты',
        hint: 'Система нашла повод присмотреться. Решение — за вами.',
        count: o => plainCount(o.candidateCount, n => `Кандидаты: ${n} вещей в очереди`),
    },
    {
        key: 'decisions',
        label: 'На рассмотрении',
        hint: 'Разбор по одной вещи: оставить, переназначить или расстаться. Решение обратимо, пока вещь у вас.',
        count: decisionsCount,
    },
    {
        key: 'pending',
        label: 'Ждут передачи',
        hint: 'Решение принято, вещи ещё у вас. Отметьте, что с ними произошло.',
        count: o => plainCount(o.pendingCount, n => `Ждут передачи: ${n} вещей с принятым решением`),
    },
    {
        key: 'archive',
        label: 'Выбыли',
        hint: 'Вещи покинули гардероб. Виден способ, дата и источник решения.',
        count: o => plainCount(o.retiredCount, n => `Выбыли: ${n} завершённых событий`),
    },
];

const DEFAULT_STAGE: DisposalStage = 'candidates';

export const isDisposalStage = (value: string | undefined): value is DisposalStage =>
    DISPOSAL_STAGES.some(s => s.key === value);

const DisposalCenterPage: React.FC = () => {
    const {stage} = useParams<{stage?: string}>();
    const {selectedOwnerId, selectedOwnerDisplayName, isOwnWardrobe} = useWardrobe();
    const [overview, setOverview] = useState<DisposalOverview | null>(null);

    useDocumentTitle();

    const loadOverview = useCallback(async () => {
        try {
            setOverview(await fetchDisposalOverview(selectedOwnerId));
        } catch (e) {
            // Счётчики — подсказка, а не содержимое: без них вкладки остаются рабочими.
            logger.debug('DisposalCenterPage: overview failed', {error: (e as Error)?.message});
            setOverview(null);
        }
    }, [selectedOwnerId]);

    useEffect(() => {
        void loadOverview();
    }, [loadOverview]);

    if (!isDisposalStage(stage)) {
        return <Navigate to={`/disposal/${DEFAULT_STAGE}`} replace/>;
    }

    const active = DISPOSAL_STAGES.find(s => s.key === stage)!;
    const ownerSuffix = !isOwnWardrobe && (selectedOwnerDisplayName
        || (selectedOwnerId ? `${selectedOwnerId.slice(0, 8)}…` : ''));

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Выбытие{ownerSuffix ? `: ${ownerSuffix}` : ''}
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-testid="disposal-stage-hint">
                    {active.hint}
                </p>
            </div>

            <nav aria-label="Стадии выбытия" className="border-b border-gray-200 dark:border-white/10">
                <ul className="flex flex-wrap gap-1 -mb-px">
                    {DISPOSAL_STAGES.map(s => {
                        const count = overview ? s.count(overview) : null;
                        const current = s.key === stage;
                        return (
                            <li key={s.key}>
                                <Link
                                    to={`/disposal/${s.key}`}
                                    data-testid={`disposal-tab-${s.key}`}
                                    aria-current={current ? 'page' : undefined}
                                    className={`inline-flex items-center gap-2 min-h-[44px] px-4 text-sm
                                                font-medium border-b-2 transition-colors duration-ui ease-out ${
                                        current
                                            ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 ' +
                                              'hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                                >
                                    <span>{s.label}</span>
                                    {count && (
                                        <span
                                            data-testid={`disposal-count-${s.key}`}
                                            aria-label={count.label}
                                            title={count.label}
                                            className="rounded-full bg-gray-200 dark:bg-white/10 px-1.5 py-0.5
                                                       text-xs tabular-nums text-gray-700 dark:text-gray-300"
                                        >
                                            {count.text}
                                        </span>
                                    )}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {overview && overview.reassignedCount > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="disposal-reassigned">
                    Домашних и рабочих: {pluralItems(overview.reassignedCount)}. Это смена назначения,
                    а не выбытие — вещи остались в гардеробе и в стадии выше не входят.
                </p>
            )}

            <StageBody stage={stage}/>
        </div>
    );
};

/** Тело стадии. Каждая стадия остаётся самостоятельным экраном со своими состояниями. */
const StageBody: React.FC<{stage: DisposalStage}> = ({stage}) => {
    if (stage === 'decisions') return <DeclutterPage/>;
    if (stage === 'pending') return <TrashBinPage/>;
    if (stage === 'archive') return <DisposedItemsPage/>;
    return <DisposalQueuePage/>;
};

export default DisposalCenterPage;
